import { useState, useEffect, useCallback, useRef } from 'react'
import type { AppCore } from '@core/bootstrap'
import { db } from '@core/db/db'
import { META_MERCURY_LOGIN, META_MERCURY_PASSWORD } from '../meta'
import type { Vsd } from '../index'
import { btn, btnBase, btnSize, cx, hint, table, tableWrap, td, th } from '@ui/classes'
import { badgeIncoming, badgeRepaid, credInput, errBanner, okBanner } from '@ui/admin'

export function MercurySection({ core }: { core: AppCore }) {
  const { mercury } = core
  const [login, setLogin] = useState('')
  const [password, setPassword] = useState('')
  const [vsd, setVsd] = useState<Vsd[]>([])
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const loadVsd = useCallback(() => {
    void mercury
      .listVsd()
      .then(setVsd)
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)))
  }, [mercury])

  useEffect(() => {
    const creds = mercury.getCredentials()
    setLogin(creds.login)
    setPassword(creds.password)
    loadVsd()
  }, [mercury, loadVsd])

  useEffect(() => {
    return () => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current)
      }
    }
  }, [])

  /**
   * Мгновенное (дебаунс 400мс) сохранение учётных данных в Dexie meta и плагин
   * при каждом изменении поля — отдельной кнопки «Сохранить» нет.
   */
  const persistCredentials = useCallback(
    (nextLogin: string, nextPassword: string) => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current)
      }
      saveTimer.current = setTimeout(() => {
        void (async () => {
          try {
            await db.meta.put({ key: META_MERCURY_LOGIN, value: nextLogin })
            await db.meta.put({ key: META_MERCURY_PASSWORD, value: nextPassword })
            mercury.setCredentials({ login: nextLogin, password: nextPassword })
            setNote('Учётные данные ВетИС сохранены')
          } catch (e: unknown) {
            setErr(e instanceof Error ? e.message : String(e))
          }
        })()
      }, 400)
    },
    [mercury]
  )

  const onLoginChange = (value: string) => {
    setLogin(value)
    persistCredentials(value, password)
  }

  const onPasswordChange = (value: string) => {
    setPassword(value)
    persistCredentials(login, value)
  }

  const repayAll = async () => {
    setBusy(true)
    setErr(null)
    setNote(null)
    try {
      const res = await mercury.repayAll()
      loadVsd()
      setNote(
        res.repaid > 0
          ? `Успешно погашено ${res.repaid} документов без использования ЭЦП`
          : 'Нет оформленных ВСД для гашения'
      )
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const incomingCount = vsd.filter((v) => v.status === 'incoming').length

  return (
    <>
      {note && <p className={okBanner}>{note}</p>}
      {err && <p className={errBanner}>{err}</p>}

      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-small">
          Логин API ВетИС
          <input
            value={login}
            onChange={(e) => onLoginChange(e.target.value)}
            className={credInput}
          />
        </label>
        <label className="flex flex-col gap-1 text-small">
          Пароль API ВетИС
          <input
            type="password"
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            className={credInput}
          />
        </label>
      </div>
      <p className={hint}>
        Схема «логин + пароль API» — без ЭЦП/КриптоПро на сервере. Изменения
        сохраняются автоматически.
      </p>

      <div className="my-3 flex items-center gap-3">
        <strong>Оформленные ВСД: {incomingCount}</strong>
        <button
          onClick={() => void repayAll()}
          disabled={busy || incomingCount === 0}
          className={cx(btnBase, btn.secondary, btnSize.sm)}
        >
          {busy ? 'Гашение…' : 'Погасить все накладные'}
        </button>
      </div>

      {vsd.length === 0 ? (
        <p className={hint}>Входящих ВСД нет.</p>
      ) : (
        <div className={tableWrap}>
          <table className={table}>
            <thead>
              <tr>
                <th className={th}>№ ВСД</th>
                <th className={th}>Продукция</th>
                <th className={th}>Объём</th>
                <th className={th}>Отправитель</th>
                <th className={th}>Статус</th>
              </tr>
            </thead>
            <tbody>
              {vsd.map((v) => (
                <tr key={v.uuid}>
                  <td className={td}>{v.uuid}</td>
                  <td className={td}>{v.product}</td>
                  <td className={td}>{v.volume}</td>
                  <td className={td}>{v.sender}</td>
                  <td className={td}>
                    {v.status === 'repaid' ? (
                      <span className={badgeRepaid}>Погашено</span>
                    ) : (
                      <span className={badgeIncoming}>Оформлено</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
